import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  TextField,
  MenuItem,
  Autocomplete,
  Grid,
  Typography,
  alpha,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import {
  countries,
  Occupations,
  Industries,
  Funds,
  companyStructures,
  accountPurposes,
  sourceOfFunds,
} from "../constants";
import {
  allowOnlyAlphabetsCityState,
  allowOnlyAlphaNumSlash,
  allowWebsiteUrl,
  OnlyAllowedInput,
  OnlyAllowedInputBankPartner,
  OnlyAllowedInputZipCode,
  OnlyAllowedZipCode,
} from ".";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";

const CompanyInformation = ({
  handleSteps,
  handleBack,
  userDetails,
  setIsloading,
}) => {
  const { btncolor } = useSelector((state) => state);
  const uuid = localStorage.getItem("uuid");
  const theme = localStorage.getItem("theme");
  const token = localStorage.getItem("access_token");
  const homeCurrencySymbol = localStorage.getItem("homeCurrencySymbol");
  const [sameAddress, setSameAddress] = useState(false);
  const [mccList, setMccList] = useState([]);

  useEffect(() => {
    const fetchMccList = async () => {
      try {
        const response = await axios.get(
          `https://accounts.paybito.com/api/user/getMerchantCategoryCodes/${uuid}`,
          {
            headers: {
              Authorization: `BEARER ${token}`,
            },
          }
        );
        if (response.data && response.data.mccList) {
          setMccList(response.data.mccList);
        }
      } catch (err) {
        console.error("Failed to fetch MCC list:", err);
      }
    };
    if (uuid) {
      fetchMccList();
    }
  }, [uuid]);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: { corporateStructure: "", accountPurpose: "" },
  });

  const [isChanged, setIsChanged] = useState(false);
  const watchedValues = watch();

  const amountData = [
    { value: "1", label: `${homeCurrencySymbol}0 - 10,000` },
    { value: "2", label: `${homeCurrencySymbol}10,000 - ${homeCurrencySymbol}25,000` },
    { value: "3", label: `${homeCurrencySymbol}25,000 - ${homeCurrencySymbol}50,000` },
    { value: "4", label: `${homeCurrencySymbol}50,000 - ${homeCurrencySymbol}100,000` },
    { value: "5", label: `${homeCurrencySymbol}100,000 - ${homeCurrencySymbol}250,000` },
    { value: "6", label: `${homeCurrencySymbol}250,000 - ${homeCurrencySymbol}1,000,000` },
    { value: "7", label: `${homeCurrencySymbol}1,000,000+` },
  ];

  const amountDataLarge = [
    { value: "1", label: `${homeCurrencySymbol}0 - 100,000` },
    { value: "2", label: `${homeCurrencySymbol}100,000 - ${homeCurrencySymbol}250,000` },
    { value: "3", label: `${homeCurrencySymbol}250,000 - ${homeCurrencySymbol}500,000` },
    { value: "4", label: `${homeCurrencySymbol}500,000 - ${homeCurrencySymbol}1,000,000` },
    { value: "5", label: `${homeCurrencySymbol}1,000,000 - ${homeCurrencySymbol}2,500,000` },
    { value: "6", label: `${homeCurrencySymbol}2,500,000 - ${homeCurrencySymbol}10,000,000` },
    { value: "7", label: `${homeCurrencySymbol}10,000,000+` },
  ];

  useEffect(() => {
    if (
      userDetails?.enterpriseUser?.corporateStructure &&
      userDetails?.enterpriseUser?.accountPurpose
    ) {
      const [structure, other] =
        userDetails.enterpriseUser.corporateStructure.split("-");

      const [structureAccount, otherAccount] =
        userDetails.enterpriseUser.accountPurpose.split("-");

      reset({
        ...userDetails.enterpriseUser,
        corporateStructure:
          structure === "Others"
            ? structure?.trim() || ""
            : userDetails.enterpriseUser.corporateStructure,
        corporateStructureOther: other || "",
        accountPurpose:
          structureAccount === "Others"
            ? structureAccount?.trim() || ""
            : userDetails.enterpriseUser.accountPurpose,
        accountPurposeOther: otherAccount || "",
      });
    }
  }, [userDetails?.enterpriseUser]);

  useEffect(() => {
    const hasChanged = Object.keys(watchedValues).some(
      (key) => watchedValues[key] !== userDetails?.enterpriseUser?.[key]
    );
    setIsChanged(hasChanged);
  }, [watchedValues, userDetails]);

  useEffect(() => {
    const isSame =
      watch("companyregAddress") === watch("companyOfficeAddress") &&
      watch("companyregCity") === watch("companyOfficeCity") &&
      watch("companyregState") === watch("companyOfficeState") &&
      watch("companyregCountry") === watch("companyOfficeCountry") &&
      watch("companyregZip") === watch("companyOfficeZip");

    if (isSame) {
      setSameAddress(true);
    }
  }, []);

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setSameAddress(isChecked);

    if (isChecked) {
      setValue("companyOfficeAddress", watch("companyregAddress"));
      setValue("companyOfficeCity", watch("companyregCity"));
      setValue("companyOfficeState", watch("companyregState"));
      setValue("companyOfficeCountry", watch("companyregCountry"));
      setValue("companyOfficeZip", watch("companyregZip"));
    } else {
      setValue("companyOfficeAddress", "");
      setValue("companyOfficeCity", "");
      setValue("companyOfficeState", "");
      setValue("companyOfficeCountry", "");
      setValue("companyOfficeZip", "");
    }
  };

  const onSubmit = async (data, action) => {
    const preliminarySaveObjectPayloadComp = {
      companyName: data?.companyName,
      companyRegNo: data?.companyRegNo,
      incorporationCountry: data?.incorporationCountry,
      companyWebsite: data?.companyWebsite,
      companyregAddress: data?.companyregAddress,
      companyregCity: data?.companyregCity,
      companyregState: data?.companyregState,
      companyregZip: data?.companyregZip,
      companyregCountry: data?.companyregCountry,
      companyOfficeAddress: data?.companyOfficeAddress,
      companyOfficeCity: data?.companyOfficeCity,
      companyOfficeState: data?.companyOfficeState,
      companyOfficeZip: data?.companyOfficeZip,
      companyOfficeCountry: data?.companyOfficeCountry,
      businessActivity: data?.businessActivity,
      accountPurpose: data?.accountPurpose,
      investmentSource: data?.investmentSource,
      revenue: data?.revenue,
      profit: data?.profit,
      companyNetWorth: data?.companyNetWorth,
      companyAssets: data?.companyAssets,
      transactionVolumes: data?.transactionVolumes,
      transactionFrequency: data?.transactionFrequency,
      bankingPartner: data?.bankingPartner,
      corporateStructure:
        data?.corporateStructure === "Others"
          ? `Others-${watch("corporateStructureOther")}`
          : data?.corporateStructure,
      relationWithBank: data?.relationWithBank,
      businessDescription: data?.businessDescription,
      accountPurpose:
        data?.accountPurpose === "Others"
          ? `Others-${watch("accountPurposeOther")}`
          : data?.accountPurpose,
    };

    setIsloading(true);

    axios
      .post(
        `${WEBSERVICE}user/saveEnterpriseUserInfo?uuid=${localStorage.getItem(
          "uuid"
        )}`,
        preliminarySaveObjectPayloadComp,
        {
          headers: {
            Authorization: `BEARER ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((result) => {
        setIsloading(false);
        if (result.data.error.error_data !== 0) {
          showToast("error", result.data.error.error_msg);
        } else {
          showToast("success", "Company Basic information saved successfully");
          setIsloading(false);
        }
      })
      .catch((error) => {
        setIsloading(false);
      });

    handleSteps();
  };

  const allowedRegexStreet = /^[a-zA-Z\s]$/;
  const handleMaxLength = (e) => {
    const key = e.key;
    if (
      key === "Backspace" ||
      key === "Tab" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "Delete"
    ) {
      return;
    }
    if (!allowedRegexStreet.test(key)) {
      e.preventDefault();
    }
    const value = e.target.value;
    if (value.length >= 350) {
      e.preventDefault();
    }
  };

  return (
    <Box sx={card}>
      {/* Step Header */}
      <Box sx={stepHeader}>
        <Box sx={{ ...stepBadge, backgroundColor: alpha(btncolor || "#1976d2", 0.12), color: btncolor }}>
          <BusinessOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={stepTitle}>Company Information</Typography>
          <Typography sx={stepCaption}>
            Provide your company's basic, address, and financial details
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 3 }} />

      <form onSubmit={handleSubmit((data) => {})}>

        {/* ── Basic Information ─────────────────────────────────── */}
        <Typography sx={sectionLabel}>Basic Information</Typography>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off"
              fullWidth
              label="Company name *"
              variant="outlined"
              {...register("companyName", { required: "This field is required" })}
              onKeyDown={allowOnlyAlphaNumSlash}
              error={!!errors.companyName}
              helperText={errors.companyName?.message}
              focused={watch("companyName")}
              sx={mf}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off"
              fullWidth
              label="Company registration number *"
              variant="outlined"
              {...register("companyRegNo", { required: "This field is required" })}
              onKeyDown={allowOnlyAlphaNumSlash}
              error={!!errors.registrationNumber}
              helperText={errors.registrationNumber?.message}
              focused={watch("companyRegNo")}
              sx={mf}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off"
              fullWidth
              select
              label="Country of incorporation *"
              variant="outlined"
              {...register("incorporationCountry", { required: "Country is required" })}
              error={!!errors.countryOfIncorporation}
              helperText={errors.countryOfIncorporation?.message}
              focused={watch("incorporationCountry")}
              sx={mf}
              defaultValue={userDetails?.enterpriseUser?.incorporationCountry}
            >
              {countries.map((option) => (
                <MenuItem key={option.code} value={option.name}>{option.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off"
              fullWidth
              label="Company website (if applicable)"
              variant="outlined"
              {...register("companyWebsite")}
              onKeyDown={allowWebsiteUrl}
              error={!!errors.companyWebsite}
              helperText={errors.companyWebsite?.message}
              focused={watch("companyWebsite")}
              sx={mf}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off"
              fullWidth
              select
              label="Corporate Structure *"
              variant="outlined"
              {...register("corporateStructure", { required: "Corporate structure is required" })}
              error={!!errors.corporateStructure}
              helperText={errors.corporateStructure?.message}
              focused={watch("corporateStructure")}
              sx={mf}
              value={watch("corporateStructure")}
            >
              {companyStructures.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {watch("corporateStructure") === "Others" ? (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                multiline
                autoComplete="off"
                fullWidth
                label="Type in words *"
                variant="outlined"
                {...register("corporateStructureOther", {
                  required: watch("corporateStructure") === "Others" ? "This field is required" : false,
                })}
                error={!!errors.corporateStructureOther}
                helperText={errors.corporateStructureOther?.message}
                focused={watch("corporateStructureOther")}
                sx={mf}
              />
            </Grid>
          ) : (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} />
          )}
        </Grid>

        {/* ── Registered Address ────────────────────────────────── */}
        <Typography sx={sectionLabel}>Company Registered Address *</Typography>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off" fullWidth label="Registered address *" variant="outlined"
              {...register("companyregAddress", { required: "This field is required" })}
              onKeyDown={allowOnlyAlphaNumSlash}
              error={!!errors.registeredAddress} helperText={errors.registeredAddress?.message}
              focused={watch("companyregAddress")} sx={mf}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off" fullWidth label="City *" variant="outlined"
              {...register("companyregCity", { required: "This field is required" })}
              onKeyDown={allowOnlyAlphabetsCityState}
              error={!!errors.registeredCity} helperText={errors.registeredCity?.message}
              focused={watch("companyregCity")} sx={mf}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off" fullWidth label="State *" variant="outlined"
              {...register("companyregState", { required: "This field is required" })}
              onKeyDown={allowOnlyAlphabetsCityState}
              error={!!errors.registeredCity} helperText={errors.registeredCity?.message}
              focused={watch("companyregState")} sx={mf}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="companyregCountry"
              control={control}
              rules={{ required: "Country is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={countries}
                  getOptionLabel={(option) => (typeof option === "string" ? option : option.name || "")}
                  isOptionEqualToValue={(option, value) =>
                    typeof value === "string" ? option.name === value : option.name === value.name
                  }
                  value={countries.find((c) => c.name === field.value) || field.value || null}
                  onChange={(_, data) => field.onChange(data ? data.name : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country *"
                      error={!!errors.registeredCountry}
                      helperText={errors.registeredCountry?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off" fullWidth label="ZIP Code / Pin Code *" variant="outlined"
              {...register("companyregZip", { required: "ZIP Code is required" })}
              onKeyDown={OnlyAllowedZipCode}
              error={!!errors.registeredZipCode} helperText={errors.registeredZipCode?.message}
              focused={watch("companyregZip")} sx={mf}
            />
          </Grid>
        </Grid>

        {/* ── Office Address ────────────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
          <Typography sx={sectionLabel}>Company Office Address *</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAddress}
                onChange={handleCheckboxChange}
                size="small"
                sx={{ color: "var(--text-color)", opacity: 0.6 }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.78rem", color: "var(--text-color)", opacity: 0.6 }}>
                Same as registered address
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        </Box>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {[
            { field: "companyOfficeAddress", label: "Office address *", keyHandler: allowOnlyAlphaNumSlash },
            { field: "companyOfficeCity", label: "City *", keyHandler: allowOnlyAlphabetsCityState },
            { field: "companyOfficeState", label: "State *", keyHandler: allowOnlyAlphabetsCityState },
          ].map(({ field, label, keyHandler }) => (
            <Grid key={field} size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                autoComplete="off" fullWidth label={label} variant="outlined"
                {...register(field, { required: "This field is required" })}
                onKeyDown={keyHandler}
                error={!!errors[field]} helperText={errors[field]?.message}
                focused={watch(field)}
                sx={{ ...mf, "& .MuiInputBase-root": { ...mf["& .MuiInputBase-root"], backgroundColor: sameAddress ? "var(--body-color-less2)" : "var(--body-color-less2)" } }}
                InputProps={{ readOnly: sameAddress }}
              />
            </Grid>
          ))}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="companyOfficeCountry"
              control={control}
              rules={{ required: "Country is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={countries}
                  disabled={sameAddress}
                  getOptionLabel={(option) => (typeof option === "string" ? option : option.name || "")}
                  isOptionEqualToValue={(option, value) =>
                    typeof value === "string" ? option.name === value : option.name === value.name
                  }
                  value={countries.find((c) => c.name === field.value) || field.value || null}
                  onChange={(_, data) => field.onChange(data ? data.name : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country *"
                      error={!!errors.companyOfficeCountry}
                      helperText={errors.companyOfficeCountry?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off" fullWidth label="ZIP Code / Pin Code *" variant="outlined"
              {...register("companyOfficeZip", { required: "ZIP Code is required" })}
              onKeyDown={OnlyAllowedZipCode}
              error={!!errors.officeZipCode} helperText={errors.officeZipCode?.message}
              focused={watch("companyOfficeZip")} sx={mf}
              InputProps={{ readOnly: sameAddress }}
            />
          </Grid>
        </Grid>

        {/* ── Industry & Business Activity ──────────────────────── */}
        <Typography sx={sectionLabel}>Industry & Business Activity</Typography>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="businessActivity"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={mccList.length > 0 ? mccList.map((item) => `${item.MCC} - ${item.DESCRIPTION}`) : Industries}
                  getOptionLabel={(option) => option || ""}
                  value={field.value || null}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Industry *"
                      error={!!errors.businessActivity}
                      helperText={errors.businessActivity?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="accountPurpose"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={accountPurposes}
                  getOptionLabel={(option) => option || ""}
                  value={field.value || null}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Purpose of account *"
                      error={!!errors.accountPurpose}
                      helperText={errors.accountPurpose?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          {watch("accountPurpose") === "Others" && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                multiline autoComplete="off" fullWidth label="Type purpose in words *" variant="outlined"
                {...register("accountPurposeOther", {
                  required: watch("accountPurpose") === "Others" ? "This field is required" : false,
                })}
                error={!!errors.accountPurposeOther} helperText={errors.accountPurposeOther?.message}
                focused={watch("accountPurposeOther")} sx={mf}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="investmentSource"
              control={control}
              rules={{ required: "Source is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={sourceOfFunds}
                  getOptionLabel={(option) => option || ""}
                  value={field.value || null}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source of investment funds *"
                      error={!!errors.investmentSource}
                      helperText={errors.investmentSource?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="revenue"
              control={control}
              rules={{ required: "Annual revenue is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={amountDataLarge}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value}
                  value={amountDataLarge.find((opt) => opt.value === field.value) || null}
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Annual revenue *"
                      error={!!errors.revenue}
                      helperText={errors.revenue?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="profit"
              control={control}
              rules={{ required: "Profit is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={amountData}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value}
                  value={amountData.find((opt) => opt.value === field.value) || null}
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Profit *"
                      error={!!errors.profit}
                      helperText={errors.profit?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="companyAssets"
              control={control}
              rules={{ required: "Total assets are required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={amountDataLarge}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value}
                  value={amountDataLarge.find((opt) => opt.value === field.value) || null}
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Total company assets *"
                      error={!!errors.companyAssets}
                      helperText={errors.companyAssets?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="companyNetWorth"
              control={control}
              rules={{ required: "Net worth is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={amountDataLarge}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value}
                  value={amountDataLarge.find((opt) => opt.value === field.value) || null}
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Net Worth *"
                      error={!!errors.companyNetWorth}
                      helperText={errors.companyNetWorth?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="transactionVolumes"
              control={control}
              rules={{ required: "Monthly volume is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={amountData}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value}
                  value={amountData.find((opt) => opt.value === field.value) || null}
                  onChange={(_, data) => field.onChange(data ? data.value : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estimated monthly transaction volumes *"
                      error={!!errors.transactionVolumes}
                      helperText={errors.transactionVolumes?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="transactionFrequency"
              control={control}
              rules={{ required: "Frequency is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={["0-10", "10-100", "100-1,000", "1,000 +"]}
                  getOptionLabel={(option) => option || ""}
                  value={field.value || null}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estimated monthly transaction frequency *"
                      error={!!errors.transactionFrequency}
                      helperText={errors.transactionFrequency?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off" fullWidth label="Current banking partner *" variant="outlined"
              {...register("bankingPartner", { required: "Banking partner is required" })}
              onKeyDown={OnlyAllowedInputBankPartner}
              error={!!errors.bankingPartner} helperText={errors.bankingPartner?.message}
              focused={watch("bankingPartner")} sx={mf}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              name="relationWithBank"
              control={control}
              rules={{ required: "Relationship duration is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={["0-1 year", "1-2 year", "2 + years"]}
                  getOptionLabel={(option) => option || ""}
                  value={field.value || null}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Banking relationship duration *"
                      error={!!errors.relationWithBank}
                      helperText={errors.relationWithBank?.message}
                      sx={mf}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              autoComplete="off" fullWidth label="Describe your business (max 350 characters) *"
              variant="outlined" multiline maxRows={4}
              {...register("businessDescription", { required: "Business description is required" })}
              onKeyDown={handleMaxLength}
              error={!!errors.businessDescription} helperText={errors.businessDescription?.message}
              focused={watch("businessDescription")} sx={mf}
            />
          </Grid>
        </Grid>
      </form>

      <Box sx={actionRow}>
        <ContainedButton
          type="submit"
          variant="contained"
          sx={{ ...submitBtn, opacity: 0.85 }}
          disabled={isChanged ? !isValid : true}
          onClick={handleSubmit((data) => onSubmit(data, "save"))}
        >
          Save and come back later
        </ContainedButton>
        <ContainedButton
          type="submit"
          variant="contained"
          sx={submitBtn}
          disabled={!isValid}
          onClick={handleSubmit((data) => onSubmit(data, "next"))}
        >
          Next
        </ContainedButton>
      </Box>
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  p: { xs: 2.5, md: 3 },
};

const stepHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mb: 2.5,
  flexWrap: "wrap",
};

const stepBadge = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const stepTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const stepCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.2,
};

const sectionLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--text-color)",
  opacity: 0.45,
  mb: 1.5,
};

// Modernized field with floating label but theme-aware
const mf = {
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiSelect-select": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.6 },
  "& .MuiInputLabel-root": { color: "var(--text-color)", opacity: 0.6 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--text-color)", opacity: 0.9 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.25)" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.45)" },
  "& .MuiFormHelperText-root": { color: "#ef4444" },
};

const actionRow = {
  mt: 3.5,
  pt: 2.5,
  borderTop: "1px solid rgba(125,125,125,0.12)",
  display: "flex",
  gap: 1.5,
  flexWrap: "wrap",
};

const submitBtn = {
  height: 48,
  px: 4,
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "0.88rem",
  textTransform: "none",
};

export default CompanyInformation;
